import { Text } from '@idaho-aeyc/ui-kit';
import { Flex, Checkbox } from 'antd';
import { useTranslation } from '@packages/utils';
import styled from 'styled-components';
import useRegistrationContext from '../../../context/NewRegistrationModalContext';
import NumberInput from '../../../components/NumberInput/NumberInput';
import { stepFiveKey } from '../../../../RegistrationModal/RegistrationModal.data';
import { TEventChildcareRequirementsUI } from '../../../../../../../data/types/events.types';

const AGE_GROUPS = [
  { value: 'ONE_TO_TWO', label: ' 1-2' },
  { value: 'THREE_TO_FIVE', label: ' 3-5' },
  { value: 'SIX_TO_TWELVE', label: ' 6-12' },
];

const AGE_SUFFIX_MAP: Record<string, string> = {
  ONE_TO_TWO: '1_2',
  THREE_TO_FIVE: '3_5',
  SIX_TO_TWELVE: '6_12',
};

const AgeGroupContainer = styled(Flex)`
  flex-wrap: wrap;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const AgeGroupItem = styled(Flex)`
  width: 50%;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 768px) {
    width: 70%;
  }
`;

const ChildCareSection = () => {
  const { t } = useTranslation({
    keyPrefix: stepFiveKey,
    lng: 'translate',
  });

  const { registrationValues, setRegistrationValues } =
    useRegistrationContext();

  const handleChildcareChange = (ageGroup: string, count: number) => {
    const updatedRequirements = registrationValues.childcareRequirements.map(
      (req: TEventChildcareRequirementsUI) => {
        if (req.name === ageGroup) {
          return {
            ...req,
            checked: count > 0,
            value: count,
          };
        }
        return req;
      },
    );

    setRegistrationValues({
      ...registrationValues,
      childcareRequirements: updatedRequirements,
    });
  };

  const handleCheckboxChange = (ageGroup: string, checked: boolean) => {
    const updatedRequirements = AGE_GROUPS.map(group => {
      const existingRequirement = registrationValues.childcareRequirements.find(
        (req: TEventChildcareRequirementsUI) => req.name === group.value,
      );

      if (group.value === ageGroup) {
        return {
          name: group.value,
          checked,
          value: checked ? 1 : 0,
        };
      }
      return (
        existingRequirement || {
          name: group.value,
          checked: false,
          value: 0,
        }
      );
    });

    setRegistrationValues({
      ...registrationValues,
      childcareRequirements: updatedRequirements,
    });
  };

  return (
    <Flex vertical gap={8} style={{ marginBottom: 32 }}>
      <h2>{t('CHILD_CARE')}</h2>
      <Text>{t('CHILD_CARE_DESCRIPTION')} </Text>

      <AgeGroupContainer>
        {AGE_GROUPS.map(age => {
          const matchingAgeGroup =
            registrationValues.childcareRequirements.find(
              (req: TEventChildcareRequirementsUI) => req.name === age.value,
            );

          const count = matchingAgeGroup ? matchingAgeGroup.value : 0;
          const isChecked = matchingAgeGroup ? matchingAgeGroup.checked : false;

          const dataCySuffix = AGE_SUFFIX_MAP[age.value] || '6_12';

          return (
            <AgeGroupItem key={age.value}>
              <Flex align="center" gap={8}>
                <Checkbox
                  checked={isChecked}
                  onChange={e =>
                    handleCheckboxChange(age.value, e.target.checked)
                  }
                  data-cy={`checkbox_ages_${dataCySuffix}`}
                />
                <Text>{`${t('AGES')}${age.label}`}</Text>
              </Flex>
              <Flex align="center" gap={8}>
                <NumberInput
                  value={count}
                  min={0}
                  max={18}
                  step={1}
                  width={40}
                  onChange={newCount =>
                    handleChildcareChange(age.value, newCount)
                  }
                  disabled={!isChecked}
                  dataCy={`input_ages_${dataCySuffix}`}
                />
              </Flex>
            </AgeGroupItem>
          );
        })}
      </AgeGroupContainer>
    </Flex>
  );
};

export default ChildCareSection;
